import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import {
  ROOM_COLUMN_WIDTH_PX,
  TIMELINE_HOUR_WIDTH_PX,
  TIMELINE_WIDTH_PX,
} from '../lib/timeline';
import { type RoomRowData } from '../lib/types';
import { RoomOverviewTimelineHeader } from './room-overview-timeline-header';
import {
  RoomOverviewRoomCell,
  RoomOverviewTimelineRow,
} from './room-timeline-row';

interface RoomOverviewBoardProps {
  rows: Array<RoomRowData>;
  shouldScrollToStart: boolean;
  onScrolledToStart: () => void;
}

interface ScrollbarSize {
  width: number;
  height: number;
}

export function RoomOverviewBoard({
  rows,
  shouldScrollToStart,
  onScrolledToStart,
}: RoomOverviewBoardProps) {
  const timelineScrollRef = useRef<HTMLDivElement>(null);
  const roomScrollRef = useRef<HTMLDivElement>(null);
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const isSyncingScrollRef = useRef(false);
  const [scrollbarSize, setScrollbarSize] = useState<ScrollbarSize>({
    width: 0,
    height: 0,
  });

  const measureScrollbarSize = useCallback(() => {
    const timeline = timelineScrollRef.current;
    if (!timeline) {
      return;
    }

    setScrollbarSize({
      width: timeline.offsetWidth - timeline.clientWidth,
      height: timeline.offsetHeight - timeline.clientHeight,
    });
  }, []);

  useLayoutEffect(() => {
    const timeline = timelineScrollRef.current;
    if (!timeline) {
      return;
    }

    measureScrollbarSize();

    const observer = new ResizeObserver(measureScrollbarSize);
    observer.observe(timeline);

    return () => {
      observer.disconnect();
    };
  }, [measureScrollbarSize]);

  const syncHeaderScrollLeft = useCallback((scrollLeft: number) => {
    const header = headerScrollRef.current;
    if (header && header.scrollLeft !== scrollLeft) {
      header.scrollLeft = scrollLeft;
    }
  }, []);

  const handleTimelineScroll = useCallback(() => {
    const timeline = timelineScrollRef.current;
    const room = roomScrollRef.current;
    if (!timeline) {
      return;
    }

    syncHeaderScrollLeft(timeline.scrollLeft);

    if (isSyncingScrollRef.current) {
      return;
    }

    if (room && room.scrollTop !== timeline.scrollTop) {
      isSyncingScrollRef.current = true;
      room.scrollTop = timeline.scrollTop;
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    }
  }, [syncHeaderScrollLeft]);

  const handleRoomScroll = useCallback(() => {
    const timeline = timelineScrollRef.current;
    const room = roomScrollRef.current;
    if (!timeline || !room || isSyncingScrollRef.current) {
      return;
    }

    if (timeline.scrollTop !== room.scrollTop) {
      isSyncingScrollRef.current = true;
      timeline.scrollTop = room.scrollTop;
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    }
  }, []);

  useEffect(() => {
    if (!shouldScrollToStart) {
      return;
    }

    const timeline = timelineScrollRef.current;
    const room = roomScrollRef.current;
    if (!timeline) {
      return;
    }

    timeline.scrollLeft = 0;
    timeline.scrollTop = 0;

    if (room) {
      room.scrollTop = 0;
    }

    syncHeaderScrollLeft(0);
    onScrolledToStart();
    measureScrollbarSize();
  }, [
    shouldScrollToStart,
    onScrolledToStart,
    syncHeaderScrollLeft,
    measureScrollbarSize,
  ]);

  return (
    <div className='flex h-[calc(100dvh-15rem)] min-h-[500px] flex-col overflow-hidden rounded-md border border-border bg-background lg:h-[calc(100dvh-19rem)]'>
      <RoomOverviewTimelineHeader
        headerScrollRef={headerScrollRef}
        verticalScrollbarWidth={scrollbarSize.width}
      />

      <div className='flex min-h-0 flex-1 overflow-hidden'>
        <div
          className='flex shrink-0 flex-col'
          style={{ width: ROOM_COLUMN_WIDTH_PX }}
        >
          <div
            ref={roomScrollRef}
            className='min-h-0 flex-1 overflow-y-auto overflow-x-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
            onScroll={handleRoomScroll}
          >
            {rows.map((row, rowIndex) => (
              <RoomOverviewRoomCell
                key={row.roomId}
                row={row}
                rowIndex={rowIndex}
              />
            ))}
          </div>
          {scrollbarSize.height > 0 && (
            <div
              className='shrink-0'
              style={{ height: scrollbarSize.height }}
              aria-hidden
            />
          )}
        </div>

        <div
          ref={timelineScrollRef}
          className='min-h-0 min-w-0 flex-1 overflow-auto'
          onScroll={handleTimelineScroll}
        >
          <div
            style={{
              width: TIMELINE_WIDTH_PX,
              minWidth: TIMELINE_WIDTH_PX,
            }}
          >
            {rows.map((row, rowIndex) => (
              <RoomOverviewTimelineRow
                key={row.roomId}
                row={row}
                rowIndex={rowIndex}
                hourWidthPx={TIMELINE_HOUR_WIDTH_PX}
                timelineWidthPx={TIMELINE_WIDTH_PX}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
